import { MidControlCodeData } from 'common/types/mid-control-code-data';
import * as Config from 'Config';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlCodeBase } from '../';
import { MidSocketInitMessage } from '../../common/types/mid-socket-init-message';
import { MIDSessionResultCode, MidSocketResponse } from '../../common/types/mid-socket-response';
import ClientDataContext from '../../store/client-data-context';
import { PhonePrefixUtil } from '../../utils/phone-prefix-utils';

interface ControlCodeProps {
  controlCodeData: MidControlCodeData;
  closeControlCode: () => void;
  setSocketErrorCode: (value: string) => void;
}

export const MidControlCode = ({ controlCodeData, closeControlCode, setSocketErrorCode }: ControlCodeProps) => {
  const [controlCode, setControlCode] = useState('');
  const [, setSocket] = useState(null as unknown as WebSocket);
  const wsRef = useRef<WebSocket | null>(null);
  const idleTimeoutRef = useRef<number | null>(null);

  const { i18n } = useTranslation();

  const clientCtx = useContext(ClientDataContext);

  const clearIdleTimeout = () => {
    if (idleTimeoutRef.current != null) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  };

  const resetIdleTimeout = (ws: WebSocket, isFinished: () => boolean, markFinished: () => void) => {
    clearIdleTimeout();
    idleTimeoutRef.current = window.setTimeout(() => {
      if (isFinished()) return;
      markFinished();

      setSocketErrorCode(MIDSessionResultCode.INTERNAL_ERROR);
      try { ws.close(1000, 'idle_timeout'); } catch {}
      closeControlCode();
    }, 150000);
  };

  useEffect(() => {
    setSocketErrorCode('');

    let finished = false;
    const isFinished = () => finished;
    const markFinished = () => { finished = true; clearIdleTimeout(); };

    const ws = new WebSocket(Config.MID_AUTH_SOCKET_URL);
    wsRef.current = ws;
    setSocket(ws);

    resetIdleTimeout(ws, isFinished, markFinished);

    ws.onopen = () => {
      resetIdleTimeout(ws, isFinished, markFinished);
      // https://github.com/micronaut-projects/micronaut-core/issues/11252
      setTimeout(() => {
        ws.send(
            JSON.stringify({
              _csrf: clientCtx.client._csrf,
              id_number: controlCodeData.authData?.personCode.trim(),
              phone_number:
                  PhonePrefixUtil.getPrefixByCountryCode(controlCodeData.authData?.authCountryCode ?? '') +
                  controlCodeData.authData?.phoneNumber.trim(),
              language: i18n.language.split('-')[0], // use only the first part of the language code, if there is a dash
            } as MidSocketInitMessage)
        );
      }, 50)
    };

    ws.onmessage = (e) => {
      resetIdleTimeout(ws, isFinished, markFinished);
      const response: MidSocketResponse = JSON.parse(e.data);
      if (response.control_code != null) {
        setControlCode(response.control_code);
      } else if (response.error) {
        setSocketErrorCode(response.error);
        markFinished();
        try { ws.close(1000, 'client_close'); } catch {}
        closeControlCode();
      } else if (response.finished && response.result != undefined) {
        if (response.result == MIDSessionResultCode.OK) {
          markFinished();
          ws.close(1000, 'MID result OK, redirecting');
          window.location.href = Config.AUTHORIZE_URL + '?' + clientCtx.client.authorizeRequestParams;
        } else {
          setSocketErrorCode(response.result);
          markFinished();
          try { ws.close(1000, 'client_close'); } catch {}
          closeControlCode();
        }
      }
    };

    ws.onerror = () => {
      setSocketErrorCode(MIDSessionResultCode.INTERNAL_ERROR);
      markFinished();
      try { ws.close(1000, 'client_close'); } catch {}
      closeControlCode();
    };

    ws.onclose = (event) => {
      if (isFinished()) return;

      if (
          event.code === 1000 &&
          (
              event.reason === 'MID result OK, redirecting' ||
              event.reason === 'user_cancel' ||
              event.reason === 'client_close' ||
              event.reason === 'component_unmount' ||
              event.reason === 'idle_timeout'
          )
      ) {
        markFinished();
        return;
      }

      markFinished();
      setSocketErrorCode(MIDSessionResultCode.INTERNAL_ERROR);
      closeControlCode();
    };

    return () => {
      markFinished();
      try { ws.close(1000, 'component_unmount'); } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ControlCodeBase
      authMethod="mid"
      isDeviceLink={false}
      controlCode={controlCode}
      onCancel={() => {
        setSocketErrorCode(MIDSessionResultCode.USER_CANCELLED);
        clearIdleTimeout();
        try { wsRef.current?.close(1000, 'user_cancel'); } catch {}
        wsRef.current = null;
        closeControlCode();
      }}
    ></ControlCodeBase>
  );
};
